<script lang="ts">
    import { getContext, onDestroy } from 'svelte'
    import MaskInput from 'svelte-input-mask'
    import { get } from 'svelte/store'
    import type { Readable } from 'svelte/store'

    import type { GenOrder, IOrder } from '../../order'
    import { DateTime } from 'luxon'
    import * as yup from 'yup'

    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import type { SimpleFetchResult } from 'hydria/services/session'
    import { luhnChecksum } from 'hydria/utils/luhn'

    import { cardData } from 'wdom/utils/credit_card'

    const orderId = getContext('orderId') as Readable<string>
    const Order = getContext('Order') as GenOrder

    interface Props {
        onSuccess: (ptype: string) => void
    }

    let { onSuccess }: Props = $props()

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    type Mode = 'tpv' | 'beanstream'
    interface IForm {
        mode: Mode
        card_number: string
        name_on_card: string
        year: number | null
        month: number | null
        cvd?: string
        auth_code?: string
        user_instructions: string
    }

    let form: IForm = $state({
        mode: 'beanstream',
        user_instructions: '',
        name_on_card: '',
        card_number: '',
        month: null,
        year: null,
        cvd: '',
        auth_code: '',
    })

    let now = DateTime.now()
    // let minExp = DateTime.local(now.year, now.month)
    let maxYear = now.year + 8

    let schema = yup.object().shape({
        user_instructions: yup.string(),
        name_on_card: yup.string().required().min(2),
        card_number: yup
            .string()
            // .min(13)
            // .max(16)
            .required()
            .matches(/^\d+$/)
            .test('cardValid', (number: string) => {
                const cdata = cardData(number)
                if (cdata == null) return false
                if (!cdata.lengths.includes(number.length)) return false
                if (luhnChecksum(number) !== 0) return false
                return true
            }),
        // .test('luhn', (value: string) => luhnChecksum(value) === 0),
        year: yup.number().required().max(maxYear).min(now.year),
        // .max(maxYear - 2000)
        // .min(now.year - 2000),
        month: yup
            .number()
            .required()
            .max(12)
            .when('year', ([year], lschema: yup.NumberSchema) => {
                if (year + 2000 > now.year) return lschema.min(1)
                return lschema.min(now.month)
            }),
        cvd: yup.string().when(['mode', 'number'], ([mode, number], lschema: yup.StringSchema) => {
            if (mode == 'beanstream') {
                const cdata = cardData(number)
                if (cdata) {
                    return lschema.required().min(cdata.cvdLength).max(cdata.cvdLength)
                }
            }
            return yup.string()
        }),
        auth_code: yup.string().optional(),
        // .when('mode', (mode: string, lschema: yup.StringSchema) => {
        //     if (mode == 'tpv') {
        //         return lschema
        //     }
        //     return lschema
        // }),
    })
    let error: string | undefined = $state(undefined)

    // Helper function to simplify that try/catch bullshit when we only want a boolean
    function validateSyncAt(key: string, form: any) {
        try {
            schema.validateSyncAt(key, form)
            return true
        } catch {
            return false
        }
    }

    let order: IOrder = $state(get(Order.orderStore))
    unsubs.push(
        Order.orderStore.subscribe((v) => {
            order = v
            form.user_instructions = order.user_instructions ? order.user_instructions : ''
        }),
    )

    let formIsValid = $derived(schema.isValidSync(form))
    let user_instructions_isValid = $derived(validateSyncAt('user_instructions', form))
    let name_on_card_isValid = $derived(validateSyncAt('name_on_card', form))
    let card_number_isValid = $derived(validateSyncAt('card_number', form))
    let month_isValid = $derived(validateSyncAt('month', form))
    let year_isValid = $derived(validateSyncAt('year', form))
    let cvd_isValid = $derived(validateSyncAt('cvd', form))
    let auth_code_isValid = $derived(validateSyncAt('auth_code', form))
    let working = $state(false)

    let expiration = $state('')
    $effect(() => {
        let month = parseInt(expiration.slice(0, 2))
        let year = parseInt(expiration.slice(2))
        form.month = isNaN(month) ? null : month
        form.year = isNaN(year) ? null : year < 1000 ? year + 2000 : year
    })
    let expiration_isValid = $derived(month_isValid && year_isValid)

    let cardMeta = $derived(cardData(form.card_number))
    let cardMask: string = $derived.by(() => {
        if (cardMeta == null) return '0000 0000 0000 0000'
        else {
            for (const [i, l] of cardMeta.lengths.entries()) {
                if (form.card_number.length == l) {
                    return cardMeta.masks[i]
                }
            }
            return cardMeta.masks[0]
        }
    })

    async function submit() {
        working = true
        error = undefined

        let r: SimpleFetchResult
        const baseFields = ['user_instructions', 'name_on_card', 'card_number', 'month', 'year', 'auth_code']
        const data = Object.fromEntries(
            Object.entries(form).filter(([k, v]) => {
                return (k == 'cvd' && form.mode == 'beanstream') || (k == 'auth_code' && form.mode == 'tpv') || baseFields.includes(k)
            }),
        )
        data.revision = order.revision
        data.total = order.total

        try {
            r = await simpleFetch('POST', `/json/ecom/1/order/${$orderId}/payment/${form.mode == 'beanstream' ? 'cc' : 'tpv'}`, data)
        } catch (e: any) {
            console.error('Failed to process payment:', e)
            working = false
            error = e && e.data && e.data.error ? e.data.error : '?'
            notify({
                type: 'danger',
                title: $_('Payment'),
                text: $_('Une erreur s’est produite lors du paiement : {error}', { error: error }),
            })
            return
        }

        Order.loadGenericResponse(r.data)
        if (r.data.approved) {
            // Nop
            // on s'attend à se faire déinstantier, aucun besoin de working = false.
            onSuccess('credit card')
        } else {
            working = false
            notify({
                type: 'danger',
                title: $_('Payment'),
                text: $_('L’achat a échoué : {error}', { error: r.data.error ? r.data.error : '?' }),
            })
            error = r.data.error
        }
    }
</script>

<form
    onsubmit={(e) => {
        e.preventDefault()
        submit()
    }}
    novalidate
    class="d-grid gap-2"
>
    {#if error}
        <div class="help-text alert alert-danger alert-card">
            <p>{error}</p>
        </div>
    {/if}

    {#if form.mode == 'tpv'}
        <div class="row">
            <div class="col-sm-8 offset-sm-3 text-start text-danger">
                <strong>Activer ce mode de paiement n’effectue pas un paiement</strong>.<br />
                En mode TPV, ça ne fait qu’inscrire un paiement fait autrement, par un TPV sur le bureau par exemple.
            </div>
        </div>
    {/if}
    <div class="row">
        <label for="name_on_card" class="col-sm-3 col-form-label">{$_('Name on card')}</label>
        <div class="col-sm-6">
            <input type="text" class="form-control" id="name_on_card" bind:value={form.name_on_card} disabled={working} class:is-invalid={!name_on_card_isValid} />
        </div>
        <div class="col-sm-3 text-end">
            <div class="btn-group btn-group-sm">
                <button type="button" class="btn btn-outline-secondary" class:active={form.mode == 'beanstream'} onclick={() => (form.mode = 'beanstream')} disabled={working}>Bambora</button>
                <button type="button" class="btn" class:btn-outline-secondary={form.mode != 'tpv'} class:btn-danger={form.mode == 'tpv'} class:active={form.mode == 'tpv'} onclick={() => (form.mode = 'tpv')} disabled={working}>TPV</button>
            </div>
        </div>
    </div>

    <div class="row">
        <label for="number" class="col-sm-3 col-form-label">{$_('Card number')}</label>
        <div class="col-sm-6">
            <MaskInput
                class="form-control {card_number_isValid ? '' : 'is-invalid'}"
                alwaysShowMask={false}
                mask={cardMask}
                value={form.card_number}
                disabled={working}
                on:change={({ detail }) => {
                    form.card_number = detail.inputState.unmaskedValue
                }}
            />
        </div>
        <div class="col-sm-3" style="align-self: center;">
            <span class="badge" class:text-bg-secondary={!cardMeta || cardMeta.tag != 'visa'} class:muted={!cardMeta || cardMeta.tag != 'visa'} class:text-bg-primary={cardMeta && cardMeta.tag == 'visa'}>VISA</span>
            <span class="badge" class:text-bg-secondary={!cardMeta || cardMeta.tag != 'mastercard'} class:muted={!cardMeta || cardMeta.tag != 'mastercard'} class:text-bg-primary={cardMeta && cardMeta.tag == 'mastercard'}>Mastercard</span>
            <span class="badge" class:text-bg-secondary={!cardMeta || cardMeta.tag != 'amex'} class:muted={!cardMeta || cardMeta.tag != 'amex'} class:text-bg-primary={cardMeta && cardMeta.tag == 'amex'}>Amex</span>
        </div>
    </div>

    <div class="row">
        <label for="expiration" class="col-sm-3 col-form-label">{$_('Expiration date')}</label>
        <div class="col-sm-2 clearfix">
            <MaskInput
                class="form-control {expiration_isValid ? '' : 'is-invalid'}"
                alwaysShowMask={false}
                mask={'00 / 00'}
                placeholder={$_('mm / yy')}
                value={expiration}
                disabled={working}
                on:change={({ detail }) => {
                    expiration = detail.inputState.unmaskedValue
                }}
            />
        </div>
    </div>

    {#if form.mode == 'beanstream'}
        <div class="row">
            <label for="cvd" class="col-sm-3 col-form-label">{$_('CSC')}</label>
            <div class="col-md-6">
                <div class="row">
                    <div class="col-xs-6 col-sm-6">
                        <input type="text" class="form-control" bind:value={form.cvd} id="cvd" disabled={working} maxlength={cardMeta ? cardMeta.cvdLength : 4} class:is-invalid={!cvd_isValid} />
                    </div>
                </div>
            </div>
        </div>
    {:else}
        <div class="row">
            <label for="cvd" class="col-sm-3 col-form-label">{$_('Auth code')}</label>
            <div class="col-md-6">
                <div class="row">
                    <div class="col-xs-6 col-sm-6">
                        <input type="text" class="form-control" bind:value={form.auth_code} id="cvd" disabled={working} class:is-invalid={!auth_code_isValid} />
                    </div>
                </div>
            </div>
        </div>
    {/if}

    <div class="row">
        <label for="user_instructions" class="col-sm-3 col-form-label">{$_('Instructions')}</label>
        <div class="col-sm-9">
            <textarea name="user_instructions" id="user_instructions" rows="2" placeholder={$_('Additional instructions or notes to be placed on the invoice.')} class="form-control" disabled={working} bind:value={form.user_instructions} class:is-invalid={!user_instructions_isValid}></textarea>
        </div>
    </div>
    <div class="tab-footer text-end">
        <button type="submit" class="btn" disabled={working || !formIsValid} class:btn-outline-secondary={working} class:btn-primary={!working}>
            {#if working}
                <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                {$_('Processing…')}
            {:else if form.mode == 'beanstream'}
                {$_('Process payment')}
            {:else}
                {$_('Inscribe payment')}
            {/if}
        </button>
    </div>
</form>
